import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles,
  BookOpen,
  Clock,
  User,
  FileText,
  Loader2,
  ChevronRight,
  Brain,
  Wand2,
  Upload,
  FileUp,
  File,
  X,
  Hash,
  Zap,
  FileType,
  ClipboardPaste,
  HelpCircle,
  Check,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Button } from '../components/ui/button';
import CompetencySelector from '../components/common/CompetencySelector';

import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

const DURATION_OPTIONS = [
  "30 minutes",
  "1 hour",
  "2 hours",
  "Half day (4 hours)",
  "1 day",
  "2 days",
  "3 days",
  "1 week",
  "2 weeks"
];

const TARGET_ROLES = [
  "FAIS Representative / Advisor",
  "Key Individual",
  "Compliance Officer",
  "Operations",
  "Legal / Risk",
  "Audit",
  "IT Operations",
  "Governance",
  "Human Resources",
  "Client Fulfilment and Administration",
  "Other Support Function"
];

const TOPIC_COUNT_OPTIONS = [3, 4, 5, 6, 7, 8, 10, 12];

const SPECIALISATIONS = [
  "Anti-Corruption",
  "Anti-Financial Crime",
  "Anti-Money Laundering",
  "Artificial Intelligence",
  "Compliance",
  "Conduct",
  "Cybersecurity",
  "Data Privacy",
  "Fraud",
  "GRC",
  "IT Governance",
  "Operations",
  "Risk Management",
  "Social Impact Compliance"
];

export default function AICourseGenerator() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Generation mode: 'topic' (AI generates from topic) or 'content' (from predefined content)
  const [generationMode, setGenerationMode] = useState('topic');
  
  const [formData, setFormData] = useState({
    title: '',
    overview: '',
    duration: '2 hours',
    target_roles: [],
    num_topics: 5,
    specialisations: [],
    author_name: 'Cerebro Core Admin'  // Default author for thumbnail
  });
  
  // For content-based generation
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pastedContent, setPastedContent] = useState('');
  const [contentSource, setContentSource] = useState('upload'); // 'upload' or 'paste'
  
  // Supplementary materials (study guides, notes, etc.)
  const [supplementaryFiles, setSupplementaryFiles] = useState([]);
  const supplementaryInputRef = useRef(null);
  
  // Competencies
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Please upload PDF, Word, or text files.`);
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(`File too large: ${file.name}. Maximum size is 10MB.`);
        continue;
      }
      
      // Read file content
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedFiles(prev => [...prev, {
          name: file.name,
          type: file.type,
          size: file.size,
          content: event.target.result,
          file: file
        }]);
      };
      
      if (file.type === 'text/plain') {
        reader.readAsText(file);
      } else {
        reader.readAsDataURL(file);
      }
    }
    
    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Supplementary materials handlers
  const handleSupplementaryUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/markdown'
    ];
    
    for (const file of files) {
      if (!validTypes.includes(file.type) && !file.name.endsWith('.md')) {
        toast.error(`Invalid file type: ${file.name}. Supported: PDF, Word, PPT, Excel, TXT, MD`);
        continue;
      }
      
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        toast.error(`File too large: ${file.name}. Maximum size is 50MB.`);
        continue;
      }
      
      setSupplementaryFiles(prev => [...prev, {
        name: file.name,
        type: file.type,
        size: file.size,
        file: file
      }]);
    }
    
    if (supplementaryInputRef.current) {
      supplementaryInputRef.current.value = '';
    }
  };

  const removeSupplementaryFile = (index) => {
    setSupplementaryFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }
    
    if (generationMode === 'topic') {
      if (!formData.overview.trim()) {
        toast.error('Please enter course overview/objectives');
        return;
      }
      if (formData.target_roles.length === 0) {
        toast.error('Please select at least one target audience');
        return;
      }
    } else {
      // Content mode
      if (contentSource === 'upload' && uploadedFiles.length === 0) {
        toast.error('Please upload at least one file');
        return;
      }
      if (contentSource === 'paste' && !pastedContent.trim()) {
        toast.error('Please paste your content');
        return;
      }
    }
    
    setGenerating(true);
    setProgress(5);
    setProgressText('Initializing AI course generator...');
    
    try {
      let response;
      
      if (generationMode === 'topic') {
        // Topic-based generation - immediately creates course with "generating" status
        setProgressText('Starting AI course generation...');
        setProgress(10);
        
        // Start generation - returns immediately with course_id
        // NAVI automatically generates MCQ questions based on course content
        response = await axios.post(`${API}/ai/generate-course`, {
          title: formData.title,
          overview: formData.overview,
          duration: formData.duration,
          target_roles: formData.target_roles,
          specialisations: formData.specialisations,
          num_sections: formData.num_topics,
          author_name: formData.author_name || 'Cerebro Core Admin',  // For auto-thumbnail generation
          auto_generate_thumbnail: true,  // Enable automatic thumbnail generation
          competency_ids: selectedCompetencies  // Link competencies
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        });
        
        const courseId = response.data.course_id;
        
        // Upload supplementary materials if any
        if (supplementaryFiles.length > 0 && courseId) {
          setProgressText('Uploading supplementary materials...');
          setProgress(85);
          
          for (const fileData of supplementaryFiles) {
            try {
              const materialForm = new FormData();
              materialForm.append('file', fileData.file);
              materialForm.append('title', fileData.name.replace(/\.[^/.]+$/, '')); // Remove extension for title
              
              await axios.post(`${API}/courses/${courseId}/materials`, materialForm, {
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              });
            } catch (err) {
              console.error('Failed to upload supplementary material:', err);
              toast.error(`Failed to upload: ${fileData.name}`);
            }
          }
        }
        
        // Course is now being generated in background
        // Show success and redirect to courses page
        setProgress(100);
        setProgressText('Course generation started!');
        
        toast.success('Course generation started! Check the Courses page to see progress.');
        
        setTimeout(() => {
          navigate('/admin?tab=courses');
        }, 1500);
        
        return; // Exit early - no need to wait
        
      } else {
        // Content-based generation
        setProgressText('Uploading content...');
        setProgress(15);
        
        const formDataObj = new FormData();
        formDataObj.append('title', formData.title);
        formDataObj.append('duration', formData.duration);
        formDataObj.append('target_roles', JSON.stringify(formData.target_roles));
        formDataObj.append('specialisations', JSON.stringify(formData.specialisations));
        formDataObj.append('num_topics', formData.num_topics.toString());
        
        if (contentSource === 'upload') {
          // Upload files
          for (const fileData of uploadedFiles) {
            formDataObj.append('files', fileData.file);
          }
        } else {
          // Pasted content
          formDataObj.append('content_text', pastedContent);
        }
        
        setProgressText('AI is analyzing your content...');
        setProgress(30);
        
        // Simulate progress for content-based generation
        let currentProgress = 30;
        const contentProgressInterval = setInterval(() => {
          if (currentProgress < 85) {
            currentProgress += 5;
            setProgress(currentProgress);
            
            if (currentProgress < 50) {
              setProgressText('Extracting key concepts from content...');
            } else if (currentProgress < 70) {
              setProgressText('Structuring course sections...');
            } else {
              setProgressText('Generating quiz and finalizing...');
            }
          }
        }, 4000);
        
        response = await axios.post(`${API}/ai/generate-course-from-content`, formDataObj, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000 // 5 minute timeout for content processing
        });
        
        clearInterval(contentProgressInterval);
      }
      
      setProgress(100);
      setProgressText('Course generated successfully!');
      
      toast.success('Course generated successfully!');
      
      setTimeout(() => {
        navigate(`/courses/${response.data.course_id}/ai-learn`);
      }, 1000);
      
    } catch (error) {
      console.error('Course generation failed:', error);
      
      let errorMessage = 'Failed to generate course';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Generation timed out. Please try again with simpler content.';
      } else if (error.response?.status === 502 || error.response?.status === 504) {
        errorMessage = 'Server is busy. Please try again in a moment.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast.error(errorMessage);
      setProgress(0);
      setProgressText('');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-navigate-dark">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F3C88]/50 to-blue-900/50 border-b border-[#1EBFB3]/20">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#1EBFB3] to-blue-500 rounded-2xl flex items-center justify-center">
              <Brain className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">
                Generate AI Course
              </h1>
              <p className="text-[#1EBFB3]">
                Create complete courses instantly with Claude 4.5
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="flex items-center gap-2 text-sm text-[#1EBFB3] bg-[#1EBFB3]/20 px-3 py-1.5 rounded-full">
              <Sparkles className="h-4 w-4" />
              AI-Generated Content
            </div>
            <div className="flex items-center gap-2 text-sm text-[#1EBFB3] bg-[#1EBFB3]/20 px-3 py-1.5 rounded-full">
              <BookOpen className="h-4 w-4" />
              Auto Sections & Visuals
            </div>
            <div className="flex items-center gap-2 text-sm text-[#1EBFB3] bg-[#1EBFB3]/20 px-3 py-1.5 rounded-full">
              <FileText className="h-4 w-4" />
              Quiz Included
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Admin Instructions Panel */}
        <div className="bg-gradient-to-r from-blue-900/30 to-[#1F3C88]/30 border border-blue-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <HelpCircle className="h-6 w-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">How AI Course Creation Works</h3>
              <div className="space-y-3 text-sm text-zinc-300">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-blue-400 min-w-[24px]">1.</span>
                  <span><strong>Choose a method:</strong> Generate from a topic (AI creates everything) or upload your own content (PDF, Word, or text files) for AI to structure.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-blue-400 min-w-[24px]">2.</span>
                  <span><strong>Fill in course details:</strong> Title, duration, target audience, and specialisation help the AI create relevant content.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-blue-400 min-w-[24px]">3.</span>
                  <span><strong>Generate:</strong> AI creates sections, content, visuals, and a quiz (usually 1-3 minutes).</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-blue-400 min-w-[24px]">4.</span>
                  <span><strong>After generation:</strong> You&apos;ll be redirected to the course player where you can review, edit content, and publish.</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-navigate-gold/10 border border-navigate-gold/30 rounded-lg">
                <p className="text-sm text-navigate-gold flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  <span><strong>Find your courses:</strong> After creation, courses appear in <strong>Admin → Courses</strong> menu. Use Edit Mode in the course player to make changes before publishing.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {generating ? (
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#1EBFB3] to-blue-500 rounded-full animate-pulse opacity-30" />
              <div className="absolute inset-2 bg-navigate-surface rounded-full flex items-center justify-center">
                <Wand2 className="h-10 w-10 text-[#1EBFB3] animate-bounce" />
              </div>
            </div>
            
            <h2 className="text-2xl font-heading font-bold text-white mb-2">
              Generating Your Course
            </h2>
            <p className="text-zinc-400 mb-8">{progressText}</p>
            
            <div className="max-w-md mx-auto">
              <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#1EBFB3] to-blue-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-zinc-500 mt-2">{progress}% complete</p>
            </div>
            
            <p className="text-xs text-zinc-600 mt-8">
              This may take 1-3 minutes depending on course complexity
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Generation Mode Selection */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-navigate-gold" />
                Choose Generation Method
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setGenerationMode('topic')}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    generationMode === 'topic'
                      ? 'border-[#1EBFB3] bg-[#1EBFB3]/10'
                      : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                    generationMode === 'topic' ? 'bg-[#1EBFB3]/20' : 'bg-zinc-700/50'
                  }`}>
                    <Brain className={`h-6 w-6 ${generationMode === 'topic' ? 'text-[#1EBFB3]' : 'text-zinc-400'}`} />
                  </div>
                  <h3 className={`font-semibold mb-2 ${generationMode === 'topic' ? 'text-white' : 'text-zinc-300'}`}>
                    Generate from Topic
                  </h3>
                  <p className="text-sm text-zinc-400">
                    AI creates complete course content based on your topic and objectives
                  </p>
                </button>
                
                <button
                  type="button"
                  onClick={() => setGenerationMode('content')}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    generationMode === 'content'
                      ? 'border-[#1EBFB3] bg-[#1EBFB3]/10'
                      : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                    generationMode === 'content' ? 'bg-[#1EBFB3]/20' : 'bg-zinc-700/50'
                  }`}>
                    <FileUp className={`h-6 w-6 ${generationMode === 'content' ? 'text-[#1EBFB3]' : 'text-zinc-400'}`} />
                  </div>
                  <h3 className={`font-semibold mb-2 ${generationMode === 'content' ? 'text-white' : 'text-zinc-300'}`}>
                    Generate from Content
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Upload documents or paste content for AI to structure into a course
                  </p>
                </button>
              </div>
            </div>

            {/* Basic Course Info */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-navigate-gold" />
                Course Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title" className="text-white">Course Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Anti-Corruption Compliance in South Africa"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="author_name" className="text-white flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Author Name (for Thumbnail)
                    </Label>
                    <Input
                      id="author_name"
                      value={formData.author_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                      placeholder="Cerebro Core Admin"
                      className="mt-1 bg-zinc-800 border-zinc-700"
                      data-testid="author-name-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      This name will appear on the course thumbnail
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="duration" className="text-white">Duration</Label>
                    <Select
                      value={formData.duration}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, duration: value }))}
                    >
                      <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="num_topics" className="text-white flex items-center gap-2">
                      <Hash className="h-4 w-4" />
                      Number of Topics
                    </Label>
                    <Select
                      value={formData.num_topics.toString()}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, num_topics: parseInt(value) }))}
                    >
                      <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TOPIC_COUNT_OPTIONS.map(num => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} Topics
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-white">Target Audience / Job Title</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 bg-zinc-800 border-zinc-700 text-left justify-between hover:bg-zinc-700"
                        data-testid="ai-target-audience-select"
                      >
                        <span className="truncate">
                          {formData.target_roles.length > 0 
                            ? `${formData.target_roles.length} role${formData.target_roles.length > 1 ? 's' : ''} selected`
                            : "Select target audience"
                          }
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0 bg-zinc-900 border-zinc-700" align="start">
                      {/* Select All / Clear All header */}
                      <div className="border-b border-zinc-700 p-2 flex items-center justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-navigate-gold hover:text-navigate-gold-light hover:bg-zinc-800"
                          onClick={() => setFormData(prev => ({ ...prev, target_roles: [...TARGET_ROLES] }))}
                        >
                          Select All
                        </Button>
                        {formData.target_roles.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                            onClick={() => setFormData(prev => ({ ...prev, target_roles: [] }))}
                          >
                            Clear All
                          </Button>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2">
                        {TARGET_ROLES.map(role => (
                          <label
                            key={role}
                            className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-zinc-800 cursor-pointer"
                          >
                            <Checkbox
                              checked={formData.target_roles.includes(role)}
                              onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                  ...prev,
                                  target_roles: checked
                                    ? [...prev.target_roles, role]
                                    : prev.target_roles.filter(r => r !== role)
                                }));
                              }}
                            />
                            <span className="text-sm text-zinc-200">{role}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {formData.target_roles.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.target_roles.map(role => (
                        <span
                          key={role}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30"
                        >
                          {role}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-amber-300"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              target_roles: prev.target_roles.filter(r => r !== role)
                            }))}
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <Label className="text-white">Specialisation</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 bg-zinc-800 border-zinc-700 text-left justify-between hover:bg-zinc-700"
                        data-testid="ai-specialisation-select"
                      >
                        <span className="truncate">
                          {formData.specialisations.length > 0 
                            ? `${formData.specialisations.length} selected`
                            : "Select specialisation"
                          }
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0 bg-zinc-900 border-zinc-700" align="start">
                      {/* Select All / Clear All header */}
                      <div className="border-b border-zinc-700 p-2 flex items-center justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-navigate-gold hover:text-navigate-gold-light hover:bg-zinc-800"
                          onClick={() => setFormData(prev => ({ ...prev, specialisations: [...SPECIALISATIONS] }))}
                        >
                          Select All
                        </Button>
                        {formData.specialisations.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                            onClick={() => setFormData(prev => ({ ...prev, specialisations: [] }))}
                          >
                            Clear All
                          </Button>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2">
                        {SPECIALISATIONS.map(spec => (
                          <label
                            key={spec}
                            className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-zinc-800 cursor-pointer"
                          >
                            <Checkbox
                              checked={formData.specialisations.includes(spec)}
                              onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                  ...prev,
                                  specialisations: checked
                                    ? [...prev.specialisations, spec]
                                    : prev.specialisations.filter(s => s !== spec)
                                }));
                              }}
                            />
                            <span className="text-sm text-zinc-200">{spec}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {formData.specialisations.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.specialisations.map(spec => (
                        <span
                          key={spec}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30"
                        >
                          {spec}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-amber-300"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              specialisations: prev.specialisations.filter(s => s !== spec)
                            }))}
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Competencies */}
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
                <CompetencySelector
                  selectedCompetencies={selectedCompetencies}
                  onChange={setSelectedCompetencies}
                  label="Link Competencies"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Link this course to competencies from your framework. This helps learners find relevant content.
                </p>
              </div>
            </div>

            {/* Topic-based Generation Fields */}
            {generationMode === 'topic' && (
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-[#1EBFB3]" />
                  Topic & Objectives
                </h2>
                
                <div>
                  <Label htmlFor="overview" className="text-white">
                    Course Overview / Learning Objectives *
                  </Label>
                  <Textarea
                    id="overview"
                    value={formData.overview}
                    onChange={(e) => setFormData(prev => ({ ...prev, overview: e.target.value }))}
                    placeholder="Describe what learners will achieve, key topics to cover, and any specific requirements..."
                    className="mt-1 bg-zinc-800 border-zinc-700 min-h-[150px]"
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    Be specific about learning outcomes - the AI will use this to structure the course content.
                  </p>
                </div>
              </div>
            )}

            {/* Content-based Generation Fields */}
            {generationMode === 'content' && (
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileUp className="h-5 w-5 text-[#1EBFB3]" />
                  Source Content
                </h2>
                
                {/* Content Source Toggle */}
                <div className="flex gap-2 mb-6">
                  <button
                    type="button"
                    onClick={() => setContentSource('upload')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      contentSource === 'upload'
                        ? 'bg-[#1EBFB3]/20 text-[#1EBFB3] border border-[#1EBFB3]/50'
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <Upload className="h-4 w-4" />
                    Upload Files
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentSource('paste')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      contentSource === 'paste'
                        ? 'bg-[#1EBFB3]/20 text-[#1EBFB3] border border-[#1EBFB3]/50'
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <ClipboardPaste className="h-4 w-4" />
                    Paste Content
                  </button>
                </div>
                
                {contentSource === 'upload' ? (
                  <div>
                    {/* File Upload Zone */}
                    <div 
                      className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center hover:border-[#1EBFB3]/50 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <Upload className="h-12 w-12 text-zinc-500 mx-auto mb-4" />
                      <p className="text-zinc-300 mb-2">
                        Drag & drop files here, or click to browse
                      </p>
                      <p className="text-xs text-zinc-500">
                        Supported: PDF, Word (.doc, .docx), Text files • Max 10MB each
                      </p>
                    </div>
                    
                    {/* Uploaded Files List */}
                    {uploadedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-3"
                          >
                            <div className="flex items-center gap-3">
                              <FileType className="h-5 w-5 text-[#1EBFB3]" />
                              <div>
                                <p className="text-sm text-white">{file.name}</p>
                                <p className="text-xs text-zinc-500">{formatFileSize(file.size)}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="p-1 text-zinc-400 hover:text-red-400 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="pastedContent" className="text-white">
                      Paste Your Content
                    </Label>
                    <Textarea
                      id="pastedContent"
                      value={pastedContent}
                      onChange={(e) => setPastedContent(e.target.value)}
                      placeholder="Paste your existing content here... The AI will analyze and structure it into course sections."
                      className="mt-1 bg-zinc-800 border-zinc-700 min-h-[250px] font-mono text-sm"
                    />
                    <p className="text-xs text-zinc-500 mt-2">
                      Paste text content from documents, articles, or training materials. The AI will structure it into organized course sections.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Supplementary Materials Upload Section */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-400" />
                Supplementary Materials (Optional)
              </h2>
              <p className="text-sm text-zinc-400 mb-4">
                Upload study guides, notes, or reference documents. These will be attached to the course for learners to download.
              </p>
              
              {/* Upload Zone */}
              <div 
                className="border-2 border-dashed border-zinc-700 rounded-xl p-6 text-center hover:border-blue-500/50 transition-colors cursor-pointer"
                onClick={() => supplementaryInputRef.current?.click()}
                data-testid="supplementary-upload-zone"
              >
                <input
                  ref={supplementaryInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md"
                  onChange={handleSupplementaryUpload}
                  multiple
                  className="hidden"
                  data-testid="supplementary-file-input"
                />
                <Upload className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                <p className="text-zinc-300 font-medium">Click to upload study materials</p>
                <p className="text-xs text-zinc-500 mt-1">PDF, Word, PowerPoint, Excel, TXT, MD (max 50MB each)</p>
              </div>
              
              {/* Uploaded Files List */}
              {supplementaryFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-zinc-400 font-medium">Files to attach ({supplementaryFiles.length}):</p>
                  {supplementaryFiles.map((file, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                      data-testid={`supplementary-file-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500/20 rounded flex items-center justify-center">
                          <FileText className="h-4 w-4 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-sm text-white truncate max-w-[250px]">{file.name}</p>
                          <p className="text-xs text-zinc-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeSupplementaryFile(index); }}
                        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        data-testid={`remove-supplementary-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={generating}
                className="btn-gold flex items-center gap-2 px-8 py-3 text-lg disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5" />
                    Generate Course with AI
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
